import { ArrowDown, ArrowUp, ArrowUpDown, Columns3 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { classes, ecrireStockage, lireStockage } from '../outils/classes';
import { Bouton } from './Bouton';
import { CaseACocher } from './Champ';
import styles from './Tableau.module.css';

export interface ColonneTableau<T> {
  id: string;
  entete: string;
  cellule: (ligne: T) => ReactNode;
  /** Valeur de tri ; sans elle, la colonne n'est pas triable. */
  valeurTri?: (ligne: T) => string | number | null;
  numerique?: boolean;
  /** false : colonne toujours visible. */
  masquable?: boolean;
  /** Première colonne d'en-tête de ligne (th scope=row). */
  enTeteLigne?: boolean;
}

export interface Tri {
  colonne: string;
  sens: 'asc' | 'desc';
}

export interface ProprietesTableau<T> {
  legende: string;
  colonnes: ColonneTableau<T>[];
  lignes: T[];
  cleLigne: (ligne: T) => string;
  /** Tri contrôlé (côté serveur) ; sans lui, tri local des lignes affichées. */
  tri?: Tri | null;
  onTri?: (tri: Tri) => void;
  /** Sélection de lignes. */
  selection?: Set<string>;
  onSelection?: (selection: Set<string>) => void;
  /** Clé de persistance des colonnes masquées. */
  cleColonnes?: string;
  /** Pagination par curseur. */
  pagination?: { aPrecedent: boolean; aSuivant: boolean; onPrecedent: () => void; onSuivant: () => void; enCours?: boolean; resume?: string };
  actions?: ReactNode;
}

function comparer(a: string | number | null, b: string | number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'fr', { numeric: true, sensitivity: 'base' });
}

/** Tableau de données : tri, pagination par curseur, sélection, colonnes masquables persistées. */
export function Tableau<T>({
  legende,
  colonnes,
  lignes,
  cleLigne,
  tri,
  onTri,
  selection,
  onSelection,
  cleColonnes,
  pagination,
  actions,
}: ProprietesTableau<T>) {
  const [triLocal, setTriLocal] = useState<Tri | null>(null);
  const triCourant = tri !== undefined ? tri : triLocal;
  const [masquees, setMasquees] = useState<string[]>(() => (cleColonnes ? (lireStockage<string[]>(`cc-colonnes:${cleColonnes}`) ?? []) : []));
  const visibles = colonnes.filter((c) => !masquees.includes(c.id));
  const masquables = colonnes.filter((c) => c.masquable !== false);

  const lignesTriees = useMemo(() => {
    if (!triCourant || onTri) return lignes;
    const col = colonnes.find((c) => c.id === triCourant.colonne);
    if (!col?.valeurTri) return lignes;
    const copie = [...lignes].sort((a, b) => comparer(col.valeurTri!(a), col.valeurTri!(b)));
    return triCourant.sens === 'desc' ? copie.reverse() : copie;
  }, [lignes, triCourant, colonnes, onTri]);

  const trier = (id: string) => {
    const suivant: Tri = { colonne: id, sens: triCourant?.colonne === id && triCourant.sens === 'asc' ? 'desc' : 'asc' };
    if (onTri) onTri(suivant);
    else setTriLocal(suivant);
  };

  const basculerColonne = (id: string) => {
    const nouvelles = masquees.includes(id) ? masquees.filter((m) => m !== id) : [...masquees, id];
    setMasquees(nouvelles);
    if (cleColonnes) ecrireStockage(`cc-colonnes:${cleColonnes}`, nouvelles);
  };

  const cles = lignesTriees.map(cleLigne);
  const toutesSelectionnees = selection !== undefined && cles.length > 0 && cles.every((c) => selection.has(c));
  const basculerTout = () => {
    if (!onSelection || !selection) return;
    const s = new Set(selection);
    if (toutesSelectionnees) for (const c of cles) s.delete(c);
    else for (const c of cles) s.add(c);
    onSelection(s);
  };
  const basculerLigne = (cle: string) => {
    if (!onSelection || !selection) return;
    const s = new Set(selection);
    if (s.has(cle)) s.delete(cle);
    else s.add(cle);
    onSelection(s);
  };

  return (
    <div className={styles.conteneur}>
      {actions || masquables.length > 0 || selection ? (
        <div className={styles.barre}>
          <div className={styles.pagination}>
            {selection ? (
              <span className={styles.selection} role="status">
                {selection.size === 0 ? 'Aucune ligne sélectionnée' : `${selection.size} ligne${selection.size > 1 ? 's' : ''} sélectionnée${selection.size > 1 ? 's' : ''}`}
              </span>
            ) : null}
            {actions}
          </div>
          {masquables.length > 1 ? (
            <details className={styles.colonnes}>
              <summary>
                <Columns3 aria-hidden strokeWidth={1.75} width={18} height={18} />
                Colonnes affichées
              </summary>
              <div className={styles.listeColonnes}>
                {masquables.map((c) => (
                  <CaseACocher key={c.id} libelle={c.entete} checked={!masquees.includes(c.id)} onChange={() => basculerColonne(c.id)} />
                ))}
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
      <div className={styles.defilement} tabIndex={0} role="region" aria-label={`${legende} (défilement horizontal possible)`}>
        <table className={styles.tableau}>
          <caption className="cc-masque">{legende}</caption>
          <thead>
            <tr>
              {selection ? (
                <th scope="col" className={styles.caseSelection}>
                  <input type="checkbox" checked={toutesSelectionnees} onChange={basculerTout} aria-label="Sélectionner toutes les lignes affichées" />
                </th>
              ) : null}
              {visibles.map((c) => {
                const actif = triCourant?.colonne === c.id;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    className={c.numerique ? styles.numerique : undefined}
                    aria-sort={actif ? (triCourant?.sens === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {c.valeurTri ? (
                      <button type="button" className={styles.tri} onClick={() => trier(c.id)}>
                        {c.entete}
                        {actif ? (
                          triCourant?.sens === 'asc' ? (
                            <ArrowUp aria-hidden />
                          ) : (
                            <ArrowDown aria-hidden />
                          )
                        ) : (
                          <ArrowUpDown aria-hidden />
                        )}
                        <span className="cc-masque">
                          {actif ? (triCourant?.sens === 'asc' ? ', tri croissant' : ', tri décroissant') : ', trier'}
                        </span>
                      </button>
                    ) : (
                      c.entete
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {lignesTriees.map((l) => {
              const cle = cleLigne(l);
              const choisie = selection?.has(cle) ?? false;
              return (
                <tr key={cle} aria-selected={selection ? choisie : undefined}>
                  {selection ? (
                    <td className={styles.caseSelection}>
                      <input type="checkbox" checked={choisie} onChange={() => basculerLigne(cle)} aria-label={`Sélectionner la ligne ${cle}`} />
                    </td>
                  ) : null}
                  {visibles.map((c) =>
                    c.enTeteLigne ? (
                      <th key={c.id} scope="row" className={classes(c.numerique && styles.numerique)}>
                        {c.cellule(l)}
                      </th>
                    ) : (
                      <td key={c.id} className={classes(c.numerique && styles.numerique)}>
                        {c.cellule(l)}
                      </td>
                    ),
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pagination && (pagination.aPrecedent || pagination.aSuivant || pagination.resume) ? (
        <nav className={styles.pagination} aria-label={`Pagination : ${legende}`}>
          <Bouton variante="secondaire" petit disabled={!pagination.aPrecedent || pagination.enCours} onClick={pagination.onPrecedent}>
            Page précédente
          </Bouton>
          <Bouton variante="secondaire" petit disabled={!pagination.aSuivant || pagination.enCours} onClick={pagination.onSuivant}>
            Page suivante
          </Bouton>
          {pagination.resume ? <span className={styles.selection}>{pagination.resume}</span> : null}
        </nav>
      ) : null}
    </div>
  );
}
